import React, { useState, useEffect, useMemo } from 'react';
import { getPokemonPool } from '../../data/regulations';
import { SPEED_ITEMS, SPEED_NATURES, SPEED_CONDITIONS } from '../../data/competitivePokemon';
import { getTypeColor } from '../../data/typeColors';
import QuizModal from './QuizModal';
import './SpeedQuiz.css';

function SpeedQuiz({ isOpen, onClose, regulation = 'all' }) {
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [score, setScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showBaseSpeeds, setShowBaseSpeeds] = useState(false);
  const [speedTotals, setSpeedTotals] = useState({ pokemon1: 0, pokemon2: 0 });

  const pool = useMemo(() => getPokemonPool(regulation), [regulation]);

  const generateQuestion = () => {
    // Select two different random Pokemon
    const pokemon1Index = Math.floor(Math.random() * pool.length);
    let pokemon2Index;
    do {
      pokemon2Index = Math.floor(Math.random() * pool.length);
    } while (pokemon2Index === pokemon1Index);

    const pokemon1 = pool[pokemon1Index];
    const pokemon2 = pool[pokemon2Index];

    // Randomly decide if Pokemon should have speed items (15% chance)
    const shouldHaveSpeedItem1 = Math.random() < 0.15;
    const shouldHaveSpeedItem2 = Math.random() < 0.15;

    const pokemon1Item = shouldHaveSpeedItem1 ? "Choice Scarf" : null;
    const pokemon2Item = shouldHaveSpeedItem2 ? "Choice Scarf" : null;

    // Randomly assign natures
    const speedNatures = Object.keys(SPEED_NATURES);
    const pokemon1Nature = speedNatures[Math.floor(Math.random() * speedNatures.length)];
    const pokemon2Nature = speedNatures[Math.floor(Math.random() * speedNatures.length)];

    // Randomly assign EVs (0, 252, or something in between)
    const pokemon1EVs = Math.random() > 0.5 ? 252 : Math.floor(Math.random() * 252);
    const pokemon2EVs = Math.random() > 0.5 ? 252 : Math.floor(Math.random() * 252);

    // Trick Room: probability scaled by how slow the Pokémon are.
    // Very slow mons (Amoonguss 30, Iron Hands / Calyrex-Ice 50) almost always run Trick Room.
    // Very fast mons (Zacian 148, Calyrex-Shadow 150) almost never do.
    const minSpeed = Math.min(pokemon1.baseSpeed, pokemon2.baseSpeed);
    const avgSpeed = (pokemon1.baseSpeed + pokemon2.baseSpeed) / 2;
    let trickRoomChance;
    if (minSpeed <= 50)      trickRoomChance = 0.55;  // Iron Hands, Calyrex-Ice, Amoonguss
    else if (minSpeed <= 65) trickRoomChance = 0.30;  // Incineroar, Skeledirge
    else if (avgSpeed <= 85) trickRoomChance = 0.15;  // Dragonite, Rillaboom bracket
    else if (avgSpeed >= 120) trickRoomChance = 0.03; // Speed demons — Trick Room almost never
    else                     trickRoomChance = 0.08;  // Default mid-range

    const conditions = {
      tailwind1: Math.random() < 0.10,
      tailwind2: Math.random() < 0.10,
      paralysis1: Math.random() < 0.08,
      paralysis2: Math.random() < 0.08,
      trickRoom: Math.random() < trickRoomChance
    };

    const newQuestion = {
      pokemon1: {
        ...pokemon1,
        item: pokemon1Item,
        nature: pokemon1Nature,
        evs: { speed: pokemon1EVs },
        conditions: {
          tailwind: conditions.tailwind1,
          paralysis: conditions.paralysis1
        }
      },
      pokemon2: {
        ...pokemon2,
        item: pokemon2Item,
        nature: pokemon2Nature,
        evs: { speed: pokemon2EVs },
        conditions: {
          tailwind: conditions.tailwind2,
          paralysis: conditions.paralysis2
        }
      },
      fieldConditions: {
        trickRoom: conditions.trickRoom
      }
    };

    setCurrentQuestion(newQuestion);
    setShowFeedback(false);
    setSpeedTotals({ pokemon1: 0, pokemon2: 0 });
  };

  const calculateSpeed = (pokemon, trickRoom = false) => {
    const baseSpeed = pokemon.baseSpeed;
    const natureMultiplier = SPEED_NATURES[pokemon.nature] || 1;
    const itemMultiplier = pokemon.item ? SPEED_ITEMS[pokemon.item] || 1 : 1;
    
    const statBase = Math.floor((2 * baseSpeed + 31 + Math.floor(pokemon.evs.speed / 4)) * 50 / 100 + 5);
    let speed = Math.floor(Math.floor(statBase * natureMultiplier) * itemMultiplier);
    
    // Apply battle conditions if they exist
    if (pokemon.conditions?.tailwind) {
      speed = Math.floor(speed * SPEED_CONDITIONS["Tailwind"]);
    }
    if (pokemon.conditions?.paralysis) {
      speed = Math.floor(speed * SPEED_CONDITIONS["Paralysis"]);
    }
    
    // If Trick Room is active, we'll return negative speed for proper comparison
    return trickRoom ? -speed : speed;
  };

  const checkAnswer = (selectedFaster) => {
    if (!currentQuestion || showFeedback) return;

    const pokemon1Speed = calculateSpeed(currentQuestion.pokemon1, currentQuestion.fieldConditions.trickRoom);
    const pokemon2Speed = calculateSpeed(currentQuestion.pokemon2, currentQuestion.fieldConditions.trickRoom);
    
    const isAnswerCorrect = (selectedFaster === 1 && pokemon1Speed > pokemon2Speed) ||
                           (selectedFaster === 2 && pokemon2Speed > pokemon1Speed) ||
                           (pokemon1Speed === pokemon2Speed); // Both are correct in case of tie

    setIsCorrect(isAnswerCorrect);
    setScore(prev => isAnswerCorrect ? prev + 1 : prev);
    setTotalQuestions(prev => prev + 1);
    setSpeedTotals({
      pokemon1: Math.abs(pokemon1Speed),
      pokemon2: Math.abs(pokemon2Speed)
    });
    setShowFeedback(true);
  };

  useEffect(() => {
    if (isOpen) {
      setScore(0);
      setTotalQuestions(0);
      setShowBaseSpeeds(false);
      generateQuestion();
    }
  }, [isOpen]);

  const getSpeedCalculationExplanation = (pokemon, total) => {
    const base = pokemon.baseSpeed;
    const evBonus = Math.floor(pokemon.evs.speed / 4);
    const natureMultiplier = SPEED_NATURES[pokemon.nature] || 1;
    const itemMultiplier = pokemon.item ? SPEED_ITEMS[pokemon.item] || 1 : 1;

    return (
      <div className="speed-calculation">
        <div className="speed-step">
          <span className="step-base">{base}</span>
          {evBonus > 0 && (
            <span className="step-mod">+ {evBonus} EVs = {base + evBonus}</span>
          )}
        </div>
        {natureMultiplier !== 1 && (
          <div className="speed-step speed-step--mult">
            <span className="step-arrow">↓</span>
            <span className="step-mod">× {natureMultiplier} <em>({pokemon.nature})</em></span>
          </div>
        )}
        {pokemon.item && (
          <div className="speed-step speed-step--mult">
            <span className="step-arrow">↓</span>
            <span className="step-mod">× {itemMultiplier} <em>({pokemon.item})</em></span>
          </div>
        )}
        {pokemon.conditions?.tailwind && (
          <div className="speed-step speed-step--mult speed-step--boost">
            <span className="step-arrow">↓</span>
            <span className="step-mod">× {SPEED_CONDITIONS["Tailwind"]} <em>(Tailwind)</em></span>
          </div>
        )}
        {pokemon.conditions?.paralysis && (
          <div className="speed-step speed-step--mult speed-step--nerf">
            <span className="step-arrow">↓</span>
            <span className="step-mod">× {SPEED_CONDITIONS["Paralysis"]} <em>(Paralysis)</em></span>
          </div>
        )}
        <div className="speed-step speed-step--final">
          <span className="speed-total">{total}</span>
        </div>
      </div>
    );
  };

  // Helper function to get item sprite URL
  const getItemSprite = (itemName) => {
    if (itemName === "Choice Scarf") {
      return "https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/items/choice-scarf.png";
    }
    return null;
  };

  return (
    <QuizModal isOpen={isOpen} onClose={onClose}>
      <div className="type-quiz-container">
        <div className="quiz-header">
          <h3>Speed Battle Quiz</h3>
          <div className="quiz-controls">
            <div className="score">Score: {score}/{totalQuestions}</div>
            <label className="base-speed-toggle">
              <input
                type="checkbox"
                checked={showBaseSpeeds}
                onChange={(e) => setShowBaseSpeeds(e.target.checked)}
              />
              Show Base Speeds
            </label>
            {showFeedback && (
              <button className="next-question-arrow" onClick={generateQuestion}>
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
                </svg>
              </button>
            )}
            <button className="close-button" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="quiz-content">
          {currentQuestion ? (
            <>
              <div className="pokemon-comparison">
                <div className={`pokemon-card ${
                  currentQuestion.pokemon1.conditions?.tailwind ? 'has-condition has-tailwind' : ''
                } ${
                  currentQuestion.pokemon1.conditions?.paralysis ? 'has-condition has-paralysis' : ''
                }`} onClick={() => checkAnswer(1)}
                  style={{ borderLeft: `4px solid ${getTypeColor(currentQuestion.pokemon1.types?.[0])}` }}>
                  <img 
                    src={`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${currentQuestion.pokemon1.spriteId ?? currentQuestion.pokemon1.id}.png`}
                    alt={currentQuestion.pokemon1.name}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/0.png';
                    }}
                  />
                  <h3>{currentQuestion.pokemon1.name}</h3>
                  <div className="pokemon-details">
                    {showBaseSpeeds && (
                      <div className="stat-row">
                        <span className="stat-label">Base Spd</span>
                        <span className="stat-value speed-highlight">{currentQuestion.pokemon1.baseSpeed}</span>
                      </div>
                    )}
                    <div className="stat-row">
                      <span className="stat-label">Nature</span>
                      <span className={`stat-value ${SPEED_NATURES[currentQuestion.pokemon1.nature] > 1 ? 'nature-pos' : SPEED_NATURES[currentQuestion.pokemon1.nature] < 1 ? 'nature-neg' : ''}`}>
                        {currentQuestion.pokemon1.nature}
                      </span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">Spd EVs</span>
                      <span className={`ev-chip ${currentQuestion.pokemon1.evs.speed >= 128 ? 'ev-high' : 'ev-low'}`}>
                        {currentQuestion.pokemon1.evs.speed}
                      </span>
                    </div>
                    {currentQuestion.pokemon1.item && (
                      <div className="stat-row item-row">
                        <img
                          src={getItemSprite(currentQuestion.pokemon1.item)}
                          alt={currentQuestion.pokemon1.item}
                          className="item-sprite"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <span className="item-name">{currentQuestion.pokemon1.item}</span>
                      </div>
                    )}
                    {currentQuestion.pokemon1.conditions?.tailwind && (
                      <span className="condition-active tailwind">Tailwind</span>
                    )}
                    {currentQuestion.pokemon1.conditions?.paralysis && (
                      <span className="condition-active paralysis">Paralyzed</span>
                    )}
                  </div>
                </div>

                <div className="vs">
                  {currentQuestion.fieldConditions?.trickRoom && (
                    <div className="field-condition">Trick Room</div>
                  )}
                  VS
                </div>

                <div className={`pokemon-card ${
                  currentQuestion.pokemon2.conditions?.tailwind ? 'has-condition has-tailwind' : ''
                } ${
                  currentQuestion.pokemon2.conditions?.paralysis ? 'has-condition has-paralysis' : ''
                }`} onClick={() => checkAnswer(2)}
                  style={{ borderLeft: `4px solid ${getTypeColor(currentQuestion.pokemon2.types?.[0])}` }}>
                  <img 
                    src={`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${currentQuestion.pokemon2.spriteId ?? currentQuestion.pokemon2.id}.png`}
                    alt={currentQuestion.pokemon2.name}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/0.png';
                    }}
                  />
                  <h3>{currentQuestion.pokemon2.name}</h3>
                  <div className="pokemon-details">
                    {showBaseSpeeds && (
                      <div className="stat-row">
                        <span className="stat-label">Base Spd</span>
                        <span className="stat-value speed-highlight">{currentQuestion.pokemon2.baseSpeed}</span>
                      </div>
                    )}
                    <div className="stat-row">
                      <span className="stat-label">Nature</span>
                      <span className={`stat-value ${SPEED_NATURES[currentQuestion.pokemon2.nature] > 1 ? 'nature-pos' : SPEED_NATURES[currentQuestion.pokemon2.nature] < 1 ? 'nature-neg' : ''}`}>
                        {currentQuestion.pokemon2.nature}
                      </span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">Spd EVs</span>
                      <span className={`ev-chip ${currentQuestion.pokemon2.evs.speed >= 128 ? 'ev-high' : 'ev-low'}`}>
                        {currentQuestion.pokemon2.evs.speed}
                      </span>
                    </div>
                    {currentQuestion.pokemon2.item && (
                      <div className="stat-row item-row">
                        <img
                          src={getItemSprite(currentQuestion.pokemon2.item)}
                          alt={currentQuestion.pokemon2.item}
                          className="item-sprite"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <span className="item-name">{currentQuestion.pokemon2.item}</span>
                      </div>
                    )}
                    {currentQuestion.pokemon2.conditions?.tailwind && (
                      <span className="condition-active tailwind">Tailwind</span>
                    )}
                    {currentQuestion.pokemon2.conditions?.paralysis && (
                      <span className="condition-active paralysis">Paralyzed</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="quiz-instructions">
                <p>Click on the Pokémon you think will move first in battle!</p>
              </div>

              {showFeedback && (
                <div className="feedback-container">
                  <div className={`feedback ${isCorrect ? 'correct' : 'incorrect'}`}>
                    {isCorrect ? '✓ Correct!' : '✗ Incorrect'}
                  </div>
                  <div className="speed-calculations">
                    <div className="calculation-column">
                      <h4>{currentQuestion.pokemon1.name}</h4>
                      {getSpeedCalculationExplanation(currentQuestion.pokemon1, speedTotals.pokemon1)}
                    </div>
                    <div className="calculation-column">
                      <h4>{currentQuestion.pokemon2.name}</h4>
                      {getSpeedCalculationExplanation(currentQuestion.pokemon2, speedTotals.pokemon2)}
                    </div>
                  </div>
                  <button className="next-question-btn" onClick={generateQuestion}>
                    Next Question
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                      <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
                    </svg>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="loading">Loading question...</div>
          )}
        </div>
      </div>
    </QuizModal>
  );
}


export default SpeedQuiz; 